import axios from 'axios';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, {
  Dispatch,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { QueryCache, useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';
import { useRecoilState } from 'recoil';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import clsx from 'clsx';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import SearchIcon from '@mui/icons-material/Search';
import { useSession, xapi } from '../../hooks/session';
import { SwSnackbar } from '../commonComponent/views/SwSnackbar';
import SwLoading from '../commonComponent/spinner/SwLoading';
import { SwPagination } from '../commonComponent/tableElement/SwPagination';
import {
  encodeSearchWord,
  getCurrentStartDate,
  getFirstMonthOfYear,
  timestamp2Localestring,
} from '../../common/helper';
import { KEY_BOOK_GROUP_LIST, KEY_BOOK_INFO_LIST } from '../../common/key';
import excellog from '../../images/sub/excel_icon.svg';
import {
  getBookGroupList,
  getBookInfoList,
  registerBookGroup,
} from '../../common/api/ApiBook';
import { SwBookGroup } from '../commonComponent/SwBookGroup';
import { SwBookList } from '../commonComponent/SwBookList';
import { uploadFile } from '../../common/api/ApiUpload';
import { RESULT_OK } from '../../common/resultCode';
import { SwSchoolStatTab } from '../commonComponent/SwSchoolStatTab';
import { SwUserStatDetail } from '../commonComponent/SwUserStatDetail';
import { SwSelectMonth } from '../commonComponent/SwSelectMonth';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { RwwCumulativeStatistics } from './RwwCumulativeStatistics';
import { RwwAverageStatistics } from './RwwAverageStatistics';
import { RwwAchievementStatistics } from './RwwAchievementStatistics';
import { SwEducationOfficeStatTab } from '../commonComponent/SwEducationOfficeStatTab';
import { educationOfficeInfoState } from '../../common/atom';
import { RwwMedalStatistics } from './RwwMedalStatistics';
import { RwwMemberStatistics } from './RwwMemberStatistics';

interface Props {}

export const EducationOfficeStatistics: React.FC<Props> = () => {
  const showSelectDay = false;

  const { loginVal } = useSession();
  const [educationOfficeInfo, setEducationOfficeInfo] = useRecoilState(
    educationOfficeInfoState
  );
  const [targetMonth, setTargetMonth] = useState<any>();
  const [pdfCheck, setPdfCheck] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const waitForRender = (callback: () => void, count = 3) => {
    if (count <= 0) callback();
    requestAnimationFrame(() => waitForRender(callback, count - 1));
  };

  const waitForRenderPromise = (count = 3) =>
    new Promise<void>(resolve => {
      const step = () => {
        if (count <= 0) resolve();
        else {
          requestAnimationFrame(() => {
            step();
          });
          count -= 1;
        }
      };
      step();
    });

  const captureElement = async (el: HTMLElement) => {
    await waitForRenderPromise();
    const canvas = await html2canvas(el, {
      backgroundColor: '#ffffff',
      useCORS: true,
      scale: 2,
    });
    return canvas.toDataURL('image/png');
  };

  const onClickMakingPdf = async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    setPdfCheck(true); // pdf 스타일 적용용
    await new Promise(res => {
      setTimeout(res, 500);
    }); // pdfCheck 상태 반영 대기

    const root = document.getElementById('pdf_root');
    const targets = Array.from(
      document.querySelectorAll('.pdf_screen')
    ) as HTMLElement[];
    // eslint-disable-next-line new-cap
    const pdf = new jsPDF('p', 'mm', 'a4');

    if (!root || targets.length === 0) return;

    // PDF용 스타일 적용
    root.style.width = '';
    root.style.padding = '';
    targets.forEach((t: any) => {
      t.style.padding = '';
    });

    const imageList = await Promise.all(
      targets.map((el, idx) =>
        captureElement(el).then(img => ({
          id: idx,
          img,
          el,
        }))
      )
    );

    // PDF 페이지 추가
    imageList.forEach((item, idx) => {
      const canvas = item.el;
      const width = 170;
      const height = (canvas.clientHeight * width) / canvas.clientWidth;
      pdf.addImage(item.img, 'PNG', 20, 20, width, height);
      if (idx < imageList.length - 1) {
        pdf.addPage();
      }
    });

    pdf.save(`${educationOfficeInfo.educationOfficeName}_통계.pdf`);

    // PDF용 스타일 원복
    root.style.width = '';
    root.style.padding = '';
    root.style.backgroundColor = '#fff';
    targets.forEach(t => {
      t.style.padding = '';
    });

    setPdfCheck(false); // 원래 상태 복원
    setIsGenerating(false);
  };

  useEffect(() => {
    if (pdfCheck) {
      onClickMakingPdf();
    }
  }, [pdfCheck]);

  useEffect(() => {
    const newTarget = getFirstMonthOfYear(loginVal);
    setTargetMonth(newTarget);
  }, []);

  return (
    <>
      <Paper
        sx={{
          p: '1.5rem',
          backgroundColor: '#fff', // ✅ 흰 배경 지정
          width: pdfCheck ? '1280px' : 'auto', // ✅ PDF용 크기
        }}
        id='pdf_root'
      >
        <SwEducationOfficeStatTab
          tabValue={2}
          educationOfficeInfo={educationOfficeInfo}
          setEducationOfficeInfo={setEducationOfficeInfo}
        />
        <Box className='flex_end'>
          <Button
            sx={{ ml: '1rem' }}
            color='info'
            variant='contained'
            onClick={() => setPdfCheck(true)}
          >
            PDF 다운로드
          </Button>
        </Box>
        <Box sx={{ backgroundColor: '#fff' }} className='pdf_screen'>
          <RwwMemberStatistics startDate={targetMonth} isPdf={pdfCheck} />
        </Box>
        <Paper
          className='pdf_screen'
          sx={{ mt: '2rem', backgroundColor: '#fff' }}
        >
          <RwwCumulativeStatistics startDate={targetMonth} isPdf={pdfCheck} />
        </Paper>
        <Box
          sx={{ mt: '2rem', backgroundColor: '#fff' }}
          className='pdf_screen'
        >
          <RwwAverageStatistics startDate={targetMonth} isPdf={pdfCheck} />
        </Box>
        <Box
          sx={{ mt: '2rem', backgroundColor: '#fff' }}
          className='pdf_screen'
        >
          <RwwMedalStatistics startDate={targetMonth} isPdf={pdfCheck} />
        </Box>
        <Box sx={{ mt: '2rem' }}>
          <SwSelectMonth
            onChangeMonth={item => {
              setTargetMonth(item);
            }}
            showSelectDay={showSelectDay}
          />
        </Box>
        <Box
          sx={{ mt: '0rem', backgroundColor: '#fff' }}
          className='pdf_screen'
        >
          <RwwAchievementStatistics startDate={targetMonth} isPdf={pdfCheck} />
        </Box>
      </Paper>
    </>
  );
};
